import React, {
  memo, useCallback, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { format as formatDate } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import {
  IconButton, Paper, Tooltip, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useAssetSettings } from '@nsd/fe';
import { five9LogPanel as five9LogPanelFeature } from 'src/constants/featureIdentifiers';
import { logClear } from 'src/actions/five9Actions';
import getCallDisplayNumber, { isCallInWrapUp } from 'src/utils/five9/getCallDisplayNumber';
import formatters from 'src/utils/formatters';
import isTruthyValue from 'src/utils/isTruthyValue';
import { store } from 'src/store';

/**
 * @typedef {import('src/type-defs/five9Types').Five9LogEntry} Five9LogEntry
 */

const PANEL_WIDTH = 640;
const COLLAPSED_HEIGHT = 32;
const EXPANDED_HEIGHT = 320;
const ROW_HEIGHT = 22;

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    bottom: 0,
    // Sit to the LEFT of the Five9 adapter panel so they don't overlap.
    left: '50%',
    transform: 'translateX(-50%)',
    width: PANEL_WIDTH,
    maxWidth: '90vw',
    // One below the Five9 adapter which uses tooltip + 1.
    zIndex: theme.zIndex.tooltip,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: theme.shadows[6],
    pointerEvents: 'auto'
  },
  hidden: {
    display: 'none'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: COLLAPSED_HEIGHT,
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    cursor: 'pointer',
    flex: '0 0 auto',
    userSelect: 'none'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflow: 'hidden'
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(12),
    letterSpacing: 0.4
  },
  badge: {
    fontSize: theme.typography.pxToRem(11),
    padding: theme.spacing(0, 0.75),
    borderRadius: 3,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  callBadge: {
    fontSize: theme.typography.pxToRem(11),
    padding: theme.spacing(0, 0.75),
    borderRadius: 3,
    backgroundColor: theme.palette.success.dark,
    color: theme.palette.common.white,
    maxWidth: 260,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center'
  },
  actionBtn: {
    color: theme.palette.common.white,
    padding: theme.spacing(0.25)
  },
  body: {
    height: EXPANDED_HEIGHT,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column'
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(12)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1),
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: theme.typography.pxToRem(11),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  rowSelected: {
    backgroundColor: theme.palette.action.selected
  },
  ts: { color: theme.palette.text.secondary, flex: '0 0 auto' },
  level: {
    flex: '0 0 auto',
    width: 44,
    textTransform: 'uppercase'
  },
  levelInfo: { color: theme.palette.info.main },
  levelWarn: { color: theme.palette.warning.main },
  levelError: { color: theme.palette.error.main },
  levelDebug: { color: theme.palette.text.secondary },
  type: {
    flex: '0 0 auto',
    color: theme.palette.primary.main,
    minWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  summary: {
    flex: '1 1 auto',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  detail: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    maxHeight: 140,
    overflow: 'auto',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: theme.typography.pxToRem(11),
    whiteSpace: 'pre-wrap',
    backgroundColor: theme.palette.grey[100]
  }
}));

/**
 * Format an epoch-ms timestamp as `HH:MM:SS.mmm` (local time) using
 * `date-fns` — consistent with the rest of the app's time formatting.
 *
 * @param {number} ts
 * @returns {string}
 */
const formatTime = (ts) => formatDate(new Date(ts), 'HH:mm:ss.SSS');

/**
 * Build the summary shown in the log row — the `message` if present,
 * otherwise a JSON-serialized preview of the payload (truncated).
 *
 * @param {Five9LogEntry} entry
 * @returns {string}
 */
const summarize = (entry) => {
  if (entry.message) return entry.message;
  const p = entry.payload;
  if (p == null) return '';
  if (typeof p !== 'object') return String(p);
  try {
    const s = JSON.stringify(p);
    return s.length > 200 ? `${s.slice(0, 200)}…` : s;
  } catch (_) {
    return '[unserializable]';
  }
};

// --- Header (subscribes only to primitives to avoid re-renders on every log push)
const LogPanelHeader = memo(({
  expanded, onToggle, onClear, onCopy
}) => {
  const classes = useStyles();
  const count = useSelector((s) => (s?.five9?.log?.length || 0));
  const activeCallSid = useSelector((s) => s?.five9?.activeCallSid || null);
  // Shared with the adapter header and TopBar chip so all surfaces match.
  const activeCallNumber = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    return getCallDisplayNumber(id ? s.five9.calls?.[id] : null);
  });
  const isActiveCallWrapUp = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    return isCallInWrapUp(id ? s.five9.calls?.[id] : null);
  });
  const activeCallDispatchId = useSelector((s) => {
    const id = s?.five9?.activeCallSid;
    return id ? (s.five9.calls?.[id]?.associatedDispatchId || null) : null;
  });

  const badgeText = activeCallSid
    ? `call ${activeCallNumber ? formatters.formatTelephoneNumber(activeCallNumber) : activeCallSid}${isActiveCallWrapUp ? ' — wrap-up' : ''}${activeCallDispatchId ? ` → dispatch ${activeCallDispatchId}` : ''}`
    : null;

  return (
    <div
      className={classes.header}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
      role="button"
      tabIndex={0}
    >
      <div className={classes.headerLeft}>
        <Typography component="span" className={classes.title}>Five9 Log</Typography>
        <span className={classes.badge}>{count}</span>
        {badgeText && <span className={classes.callBadge} title={badgeText}>{badgeText}</span>}
      </div>
      <div className={classes.headerActions}>
        <Tooltip title="Copy all">
          <IconButton
            size="small"
            className={classes.actionBtn}
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
            aria-label="Copy Five9 log"
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear">
          <IconButton
            size="small"
            className={classes.actionBtn}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            aria-label="Clear Five9 log"
          >
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? 'Minimize' : 'Expand'}>
          <IconButton
            size="small"
            className={classes.actionBtn}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-label={expanded ? 'Minimize Five9 log' : 'Expand Five9 log'}
          >
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

LogPanelHeader.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired
};

// --- Body (only mounted when expanded so the log subscription is inactive
// while collapsed).
function LogPanelBody({ selectedId, onSelect }) {
  const classes = useStyles();
  const log = useSelector((s) => s?.five9?.log || []);
  const selectedEntry = useMemo(
    () => (selectedId ? log.find((e) => e.id === selectedId) : null),
    [log, selectedId]
  );

  const Row = useCallback(({ index, style }) => {
    // Newest first
    const entry = log[log.length - 1 - index];
    if (!entry) return null;
    const levelClass = {
      info: classes.levelInfo,
      warn: classes.levelWarn,
      error: classes.levelError,
      debug: classes.levelDebug
    }[entry.level] || '';
    const isSelected = selectedId === entry.id;
    return (
      <div
        style={style}
        className={clsx(classes.row, isSelected && classes.rowSelected)}
        onClick={() => onSelect(isSelected ? null : entry.id)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSelect(isSelected ? null : entry.id); }}
        role="button"
        tabIndex={0}
      >
        <span className={classes.ts}>{formatTime(entry.ts)}</span>
        <span className={clsx(classes.level, levelClass)}>{entry.level}</span>
        <span className={classes.type} title={entry.type}>{entry.type}</span>
        <span className={classes.summary}>{summarize(entry)}</span>
      </div>
    );
  }, [log, classes, selectedId, onSelect]);

  if (!log.length) {
    return <div className={classes.empty}>No Five9 events yet.</div>;
  }

  const listHeight = selectedEntry ? EXPANDED_HEIGHT - 148 : EXPANDED_HEIGHT;

  return (
    <>
      <List
        height={listHeight}
        itemCount={log.length}
        itemSize={ROW_HEIGHT}
        width="100%"
      >
        {Row}
      </List>
      {selectedEntry && (
        <pre className={classes.detail}>
          {JSON.stringify(selectedEntry, null, 2)}
        </pre>
      )}
    </>
  );
}

LogPanelBody.propTypes = {
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};
LogPanelBody.defaultProps = { selectedId: null };

/**
 * In-app log panel for the Five9 integration. Docked at the bottom of the
 * viewport, starts collapsed, toggleable. When collapsed it shows only a
 * header strip with an event counter and the active call badge — the log
 * list itself is unmounted, so the expensive `log` subscription is inactive.
 *
 * @param {Object} props
 * @param {boolean} [props.hidden] - When true, the panel stays mounted but is not visible.
 * @returns {JSX.Element|null}
 */
function Five9LogPanel({ hidden }) {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const { hasFeatureAccess } = useAssetSettings();
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const envEnabled = isTruthyValue(process.env.REACT_APP_ENABLE_FIVE9_LOG_PANEL);
  const featureEnabled = !!hasFeatureAccess(five9LogPanelFeature);

  const handleToggle = useCallback(() => setExpanded((v) => !v), []);
  const handleClear = useCallback(() => {
    reduxDispatch(logClear());
    setSelectedId(null);
  }, [reduxDispatch]);
  const handleCopy = useCallback(() => {
    try {
      // Read once, on demand — do not subscribe.
      const log = store.getState()?.five9?.log || [];
      const text = log.map((e) => (
        `${new Date(e.ts).toISOString()} ${e.level.toUpperCase()} ${e.type} ${e.message} ${JSON.stringify(e.payload)}`
      )).join('\n');
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text);
      }
    } catch (_) { /* noop */ }
  }, []);

  if (!envEnabled && !featureEnabled) return null;

  const rootClassName = clsx(classes.root, hidden && classes.hidden);

  return (
    <Paper className={rootClassName} elevation={6}>
      <LogPanelHeader
        expanded={expanded}
        onToggle={handleToggle}
        onClear={handleClear}
        onCopy={handleCopy}
      />
      {expanded && (
        <div className={classes.body}>
          <LogPanelBody selectedId={selectedId} onSelect={setSelectedId} />
        </div>
      )}
    </Paper>
  );
}

Five9LogPanel.propTypes = {
  hidden: PropTypes.bool
};

Five9LogPanel.defaultProps = {
  hidden: false
};

export default Five9LogPanel;
